import React, { useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCalendarAlt,
  FaExclamationTriangle,
  FaImages,
  FaNewspaper,
  FaRegEye,
  FaSearchPlus,
  FaTimes,
  FaUserEdit,
} from "react-icons/fa";

import Reveal from "../../../components/Reveal/Reveal";
import { getNews } from "../../../services/newsServices";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";
const NEWS_PER_PAGE = 3;

const isExternalURL = (url) => {
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

const getImageUrl = (url) => {
  if (!url) return "";

  const cleanUrl = String(url).trim();

  if (!cleanUrl) return "";

  if (
    isExternalURL(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  const cleanBaseUrl = BASE_URL.replace(/\/$/, "");

  if (cleanUrl.startsWith("/")) {
    return `${cleanBaseUrl}${cleanUrl}`;
  }

  return `${cleanBaseUrl}/${cleanUrl}`;
};

const stripHtml = (html = "") => {
  if (!html) return "";

  const temp = document.createElement("div");
  temp.innerHTML = html;

  return temp.textContent || temp.innerText || "";
};

const truncateText = (text = "", max = 170) => {
  const clean = String(text).trim().replace(/\s+/g, " ");

  if (clean.length <= max) return clean;

  const slice = clean.slice(0, max);
  const cut = slice.slice(0, slice.lastIndexOf(" ")) || slice;

  return `${cut}...`;
};

const formatDate = (value) => {
  if (!value) return null;

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const getNewsDate = (news) =>
  news?.published_at ||
  news?.date_publication ||
  news?.createdAt ||
  news?.created_at ||
  news?.updatedAt ||
  news?.updated_at;

const isActiveNews = (news) => {
  if (
    news?.is_active === undefined ||
    news?.is_active === null ||
    news?.is_active === ""
  ) {
    return true;
  }

  return news.is_active === true || news.is_active === 1 || news.is_active === "1";
};

const getAuthorName = (news) => {
  if (!news?.author) return "";

  return `${news.author?.prenom || ""} ${news.author?.nom || ""}`.trim();
};

const SkeletonCard = () => (
  <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
    <div className="h-64 animate-pulse bg-slate-200 dark:bg-slate-800" />
    <div className="space-y-4 p-5">
      <div className="h-5 w-2/3 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
      <div className="h-4 w-full animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
      <div className="h-4 w-4/5 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
      <div className="h-10 w-36 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
    </div>
  </div>
);

const EmptyState = () => (
  <div className="col-span-full rounded-[2rem] border border-dashed border-slate-300 bg-white/75 p-10 text-center shadow-sm dark:border-white/10 dark:bg-white/5">
    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-primary dark:bg-orange-500/10">
      <FaNewspaper />
    </div>

    <h3 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
      Aucune actualité disponible
    </h3>

    <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-400">
      Les actualités du club apparaîtront ici dès qu’elles seront publiées dans
      l’espace d’administration.
    </p>
  </div>
);

const ErrorBanner = ({ message }) => (
  <div className="mb-8 rounded-[1.5rem] border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
    <div className="flex items-center justify-center gap-3">
      <FaExclamationTriangle />
      {message}
    </div>
  </div>
);

const ModalBackdrop = ({ children, onClose, label = "Fenêtre" }) => {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={label}
      onClick={onClose}
    >
      {children}
    </div>
  );
};

const ImageModal = ({ image, onClose }) => {
  if (!image?.src) return null;

  return (
    <ModalBackdrop onClose={onClose} label="Image agrandie">
      <div
        className="relative max-h-[92vh] max-w-6xl overflow-hidden rounded-[2rem] border border-white/10 bg-white/10 p-2 shadow-2xl backdrop-blur-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <img
          src={image.src}
          alt={image.alt || "Image d'actualité"}
          className="max-h-[86vh] w-full rounded-[1.5rem] object-contain"
        />

        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-950 shadow-lg transition hover:scale-105"
          aria-label="Fermer l'image"
        >
          <FaTimes />
        </button>
      </div>
    </ModalBackdrop>
  );
};

const ArticleModal = ({ article, onClose, onOpenImage }) => {
  if (!article) return null;

  const imageUrl = getImageUrl(article.image_url);
  const dateStr = formatDate(getNewsDate(article));
  const authorFull = getAuthorName(article);

  return (
    <ModalBackdrop onClose={onClose} label="Détail de l'actualité">
      <article
        className="relative max-h-[92vh] w-full max-w-5xl overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-2xl dark:border-white/10 dark:bg-slate-950"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-30 flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-950 shadow-lg transition hover:scale-105 dark:bg-slate-900 dark:text-white"
          aria-label="Fermer l'actualité"
        >
          <FaTimes />
        </button>

        <div className="max-h-[92vh] overflow-y-auto">
          {imageUrl && (
            <div className="relative h-[280px] overflow-hidden bg-slate-900 sm:h-[380px] lg:h-[460px]">
              <img
                src={imageUrl}
                alt={article.title || "Actualité"}
                className="h-full w-full object-cover"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/25 to-transparent" />

              <button
                type="button"
                onClick={() =>
                  onOpenImage({
                    src: imageUrl,
                    alt: article.title || "Image d'actualité",
                  })
                }
                className="absolute bottom-5 right-5 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/15 px-4 py-2 text-sm font-bold text-white backdrop-blur-xl transition hover:bg-white/25"
              >
                <FaSearchPlus />
                Agrandir l’image
              </button>
            </div>
          )}

          <div className="p-6 sm:p-8 lg:p-10">
            <div className="mb-5 flex flex-wrap items-center gap-3 text-sm text-slate-500 dark:text-slate-400">
              {dateStr && (
                <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-4 py-2 font-semibold dark:bg-white/10">
                  <FaCalendarAlt className="text-primary" />
                  {dateStr}
                </span>
              )}

              {authorFull && (
                <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-4 py-2 font-semibold dark:bg-white/10">
                  <FaUserEdit className="text-primary" />
                  {authorFull}
                </span>
              )}
            </div>

            <h3 className="font-barlow text-3xl font-bold leading-tight text-slate-950 dark:text-white sm:text-4xl lg:text-5xl">
              {article.title}
            </h3>

            <div
              className="prose prose-slate mt-7 max-w-none text-base leading-8 text-slate-600 dark:prose-invert dark:text-slate-300 prose-p:my-4 prose-a:font-bold prose-a:text-primary prose-img:rounded-2xl"
              dangerouslySetInnerHTML={{
                __html: article.content || "",
              }}
            />
          </div>
        </div>
      </article>
    </ModalBackdrop>
  );
};

const NewsCard = ({ news, index, onOpenImage, onOpenArticle }) => {
  const imageUrl = getImageUrl(news.image_url);
  const dateStr = formatDate(getNewsDate(news));
  const authorFull = getAuthorName(news);

  const shortText = useMemo(() => {
    return truncateText(stripHtml(news.content || ""), 165);
  }, [news.content]);

  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-2 hover:shadow-2xl dark:border-white/10 dark:bg-slate-900">
      <div className="relative h-64 overflow-hidden bg-slate-200 dark:bg-slate-800">
        {imageUrl ? (
          <>
            <img
              src={imageUrl}
              alt={news.title || "Actualité"}
              className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
              loading={index === 0 ? "eager" : "lazy"}
            />

            <button
              type="button"
              onClick={() =>
                onOpenImage({
                  src: imageUrl,
                  alt: news.title || "Image d'actualité",
                })
              }
              className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/15 text-white opacity-0 shadow-lg backdrop-blur-xl transition group-hover:opacity-100 hover:scale-105 hover:bg-white/25"
              aria-label="Agrandir l'image"
            >
              <FaSearchPlus />
            </button>
          </>
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-800 to-slate-950 text-white">
            <FaImages className="text-5xl opacity-70" />
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/20 to-transparent" />

        <div className="absolute bottom-4 left-4 right-4">
          {dateStr && (
            <span className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-bold text-white/85 backdrop-blur-xl">
              <FaCalendarAlt className="text-primary" />
              {dateStr}
            </span>
          )}

          <h3 className="line-clamp-2 font-barlow text-2xl font-bold leading-tight text-white drop-shadow-lg">
            {news.title}
          </h3>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-5">
        {authorFull && (
          <div className="mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
            <FaUserEdit className="text-primary" />
            {authorFull}
          </div>
        )}

        <p className="text-sm leading-7 text-slate-600 dark:text-slate-300">
          {shortText ||
            "Retrouvez les dernières informations publiées par le club."}
        </p>

        <div className="mt-auto pt-6">
          <button
            type="button"
            onClick={() => onOpenArticle(news)}
            className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-slate-950 px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-primary dark:bg-white dark:text-slate-950 dark:hover:bg-primary dark:hover:text-white"
          >
            <FaRegEye />
            Lire l’actualité
          </button>
        </div>
      </div>
    </article>
  );
};

export default function LastNews() {
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    let mounted = true;

    const fetchNews = async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        const latestNews = await getNews();

        if (!mounted) return;

        const activeNews = (Array.isArray(latestNews) ? latestNews : [])
          .filter(isActiveNews)
          .sort((a, b) => {
            const dateA = new Date(getNewsDate(a) || 0).getTime();
            const dateB = new Date(getNewsDate(b) || 0).getTime();

            return dateB - dateA;
          });

        setNewsList(activeNews);
      } catch (error) {
        console.error("Erreur lors du chargement des actualités :", error);

        if (mounted) {
          setErrorMsg("Impossible de charger les actualités.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchNews();

    return () => {
      mounted = false;
    };
  }, []);

  const pageCount = Math.max(1, Math.ceil(newsList.length / NEWS_PER_PAGE));

  const currentNews = useMemo(() => {
    const start = (currentPage - 1) * NEWS_PER_PAGE;
    return newsList.slice(start, start + NEWS_PER_PAGE);
  }, [newsList, currentPage]);

  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < pageCount;

  const goToPrevious = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const goToNext = () => {
    setCurrentPage((prev) => Math.min(pageCount, prev + 1));
  };

  return (
    <section className="relative overflow-hidden bg-white py-20 text-slate-950 dark:bg-slate-950 dark:text-white lg:py-28">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.12),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.13)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.13)_1px,transparent_1px)] bg-[size:42px_42px] opacity-35 dark:opacity-15" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
            <FaNewspaper />
            Vie du club
          </div>

          <h2 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Dernières{" "}
            <span className="text-primary">
              actualités
            </span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            Retrouvez les informations récentes, les événements, les annonces et
            les moments importants de la vie du club.
          </p>
        </Reveal>

        {errorMsg && <ErrorBanner message={errorMsg} />}

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {loading &&
            Array.from({ length: NEWS_PER_PAGE }).map((_, index) => (
              <SkeletonCard key={`news-skeleton-${index}`} />
            ))}

          {!loading && currentNews.length === 0 && <EmptyState />}

          {!loading &&
            currentNews.map((news, index) => (
              <Reveal
                key={news.id || `${news.title}-${index}`}
                variant="zoom"
                delay={index * 90}
              >
                <NewsCard
                  news={news}
                  index={index}
                  onOpenImage={setSelectedImage}
                  onOpenArticle={setSelectedArticle}
                />
              </Reveal>
            ))}
        </div>

        {!loading && newsList.length > NEWS_PER_PAGE && (
          <Reveal delay={120} className="mt-10">
            <div className="flex flex-col items-center justify-between gap-4 rounded-[2rem] border border-slate-200 bg-white/75 p-4 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5 sm:flex-row">
              <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                Page{" "}
                <span className="font-bold text-slate-950 dark:text-white">
                  {currentPage}
                </span>{" "}
                sur{" "}
                <span className="font-bold text-slate-950 dark:text-white">
                  {pageCount}
                </span>{" "}
                · {newsList.length} actualité
                {newsList.length > 1 ? "s" : ""}
              </p>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={goToPrevious}
                  disabled={!canGoPrevious}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-orange-200 hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
                >
                  <FaArrowLeft />
                  Précédent
                </button>

                <button
                  type="button"
                  onClick={goToNext}
                  disabled={!canGoNext}
                  className="inline-flex items-center gap-2 rounded-full bg-slate-950 px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-primary disabled:cursor-not-allowed disabled:opacity-40 dark:bg-white dark:text-slate-950 dark:hover:bg-primary dark:hover:text-white"
                >
                  Suivant
                  <FaArrowRight />
                </button>
              </div>
            </div>
          </Reveal>
        )}
      </div>

      <ImageModal
        image={selectedImage}
        onClose={() => setSelectedImage(null)}
      />

      <ArticleModal
        article={selectedArticle}
        onClose={() => setSelectedArticle(null)}
        onOpenImage={(image) => {
          setSelectedArticle(null);
          setSelectedImage(image);
        }}
      />
    </section>
  );
}